import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Simulation, SimulationPeer } from '../models/simskillsModels';
import { ModuleEnum } from '../utils/enums/module.enum';
import { environment } from '../../environments/environment';
import { FirebaseService } from './firebase.service';

export class SimulationFilterModel {
  public module?: string;
  public langs?: string[];
  public simulationIds?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class SimulationsService {
  private readonly baseUrl = `${environment.apiUrl}`;
  
  constructor(
    private http: HttpClient,
    private firebaseService: FirebaseService,
  ) {
  }
  
  public getSimulations(filter: SimulationFilterModel = {}): Observable<Simulation[]> {
    return this.http.get<Simulation[]>(`${this.baseUrl}/simulations`, {params: {...filter}});
  }
  
  public getSimulationById(id: string): Observable<any> {
    const url = `${this.baseUrl}/simulations/${id}`;
    return this.http.get(url);
  }
  
  // GET /api/simulations?module={module}
  public getSimulationsByModule(module: string): Observable<Simulation[]> {
    const params = new HttpParams().set('module', String(module));
    return this.http.get<Simulation[]>(`${this.baseUrl}/simulations`, {params});
  }
  
  // GET /api/simulations (all simulations)
  public getAllSimulations(): Observable<Simulation[]> {
    return this.http.get<Simulation[]>(`${this.baseUrl}/simulations`);
  }
  
  // GET /api/simulations/peers?module={module}
  public getPeersSimulations(module: ModuleEnum | string): Observable<Simulation[]> {
    const params = new HttpParams().set('module', String(module));
    return this.http.get<Simulation[]>(`${this.baseUrl}/simulations/peers`, {params});
  }
  
  // GET /api/simulations/{id}?module={module}
  public getSimulation(simulationId: string): Observable<Simulation> {
    return this.http.get<Simulation>(`${this.baseUrl}/simulations/${encodeURIComponent(simulationId)}`);
  }
  
  // GET /api/simulations/peers/{id}?module={module}
  public getPeerSimulation(module: ModuleEnum | string, simulationId: string): Observable<SimulationPeer> {
    const params = new HttpParams().set('module', String(module));
    return this.http.get<SimulationPeer>(`${this.baseUrl}/simulations/peers/${encodeURIComponent(simulationId)}`, {params});
  }
  
  // POST /api/simulations
  public addSimulation(simulation: Simulation): Observable<string> {
    return this.http.post(`${this.baseUrl}/simulations`, simulation, {responseType: 'text'});
  }
  
  // POST /api/simulations/peers
  public addPeerSimulation(module: ModuleEnum | string, simulation: SimulationPeer): Observable<string> {
    const body = {module, ...simulation};
    return this.http.post(`${this.baseUrl}/simulations/peers`, body, {responseType: 'text' as 'text'});
  }
  
  // DELETE /api/simulations/{id}?module={module}
  public removeSimulation(module: ModuleEnum | string, simulationId: string): Observable<void> {
    const params = new HttpParams().set('module', String(module));
    return this.http.delete<void>(`${this.baseUrl}/simulations/${encodeURIComponent(simulationId)}`, {params});
  }
  
  // PUT /api/simulations/{id}?module={module}
  public updateSimulation(
    simulationId: string,
    updatedData: Partial<Simulation>
  ): Observable<void> {
    return this.http.put<void>(`${this.baseUrl}/simulations/${encodeURIComponent(simulationId)}`, updatedData);
  }
  
  // PATCH /api/simulations/{id}?module={module}
  // Permite actualizar una propiedad específica: path/value
  public setSimulationValue(
    module: ModuleEnum | string,
    simulationId: string,
    path: string,
    value: any
  ): Observable<void> {
    const params = new HttpParams().set('module', String(module));
    const payload: Record<string, any> = {[path]: value};
    return this.http.patch<void>(`${this.baseUrl}/simulations/${encodeURIComponent(simulationId)}`, payload, {params});
  }
  
  // PUT /api/simulations/peers/{id}?module={module}
  public editPeerSimulation(
    module: ModuleEnum | string,
    simulationId: string,
    updatedData: Partial<SimulationPeer>
  ): Observable<void> {
    const params = new HttpParams().set('module', String(module));
    return this.http.put<void>(`${this.baseUrl}/simulations/peers/${encodeURIComponent(simulationId)}`, updatedData, {params});
  }
  
  public updateSimulationPropertyById = (id: string, property: string, data: any): Observable<any> =>
    this.http.put<any>(`${this.baseUrl}/simulations/${id}/property`, {data}, {params: {property}});
}
